import CloseIcon from "@mui/icons-material/Close"
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"

import { PRIMARY } from "@constants/colors"
import { useChangelogPageContext } from "@contexts/main/changelog_page_context"
import { listDeliveries } from "@domain/changelog"
import { Changelog, ChangelogDeliveryRow } from "@models/changelog"

import { formatShortDate, reactionEmoji } from "../shared"

function announcementTitle(row?: Changelog): string | undefined {
  if (!row) {
    return undefined
  }
  const translations = row.translations || {}
  const langs = Object.keys(translations)
  const ordered = [
    ...["tr", "en"].filter((lang) => langs.includes(lang)),
    ...langs.filter((lang) => lang !== "tr" && lang !== "en")
  ]
  return translations[ordered[0]]?.title
}

function personName(row: ChangelogDeliveryRow): string {
  const person = row.cPerson
  if (!person) {
    return "—"
  }
  const name = `${person.firstName || ""} ${person.lastName || ""}`.trim()
  return name || person.email || "—"
}

export default function AnnouncementDetailDrawer() {
  const { translate, detailTarget, isDetailOpen, closeDetail } =
    useChangelogPageContext()

  const announcementId = detailTarget?._id
  const { data, isLoading } = useQuery({
    queryKey: ["changelog-announcement-feedback", announcementId],
    queryFn: async () => {
      if (!announcementId) {
        return []
      }
      const response = await listDeliveries({
        announcementId,
        hasFeedback: true,
        pageSize: 200
      })
      return response.data?.docs ?? []
    },
    enabled: isDetailOpen && Boolean(announcementId)
  })

  const feedback = data ?? []
  const reactionAverage = detailTarget?.reactionAvg ?? 0
  const reactionCount = detailTarget?.reactionCount ?? 0

  return (
    <Drawer
      anchor="right"
      open={isDetailOpen}
      onClose={closeDetail}
      PaperProps={{ sx: { width: ["100vw", "27rem"], maxWidth: "27rem" } }}
    >
      <Stack sx={{ height: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 2, background: PRIMARY, color: "white" }}
        >
          <Typography sx={{ fontWeight: 700 }} noWrap>
            {announcementTitle(detailTarget) || translate("changelog_untitled")}
          </Typography>
          <IconButton
            size="small"
            sx={{ color: "white" }}
            onClick={closeDetail}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 2, background: "white" }}
        >
          <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
            {translate("changelog_detail_reactions")}
          </Typography>
          {reactionCount === 0 ? (
            <Typography sx={{ color: "text.secondary" }}>—</Typography>
          ) : (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography component="span" sx={{ fontSize: "1.25rem" }}>
                {reactionEmoji(reactionAverage)}
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {reactionAverage}
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                ({reactionCount})
              </Typography>
            </Stack>
          )}
        </Stack>

        <Divider />

        <Box sx={{ flex: 1, overflow: "auto", background: "white" }}>
          {isLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : feedback.length === 0 ? (
            <Stack alignItems="center" sx={{ py: 6, px: 2.5 }}>
              <Typography sx={{ color: "text.secondary", textAlign: "center" }}>
                {translate("changelog_detail_no_reactions")}
              </Typography>
            </Stack>
          ) : (
            feedback.map((item) => (
              <Box key={item._id}>
                <Box sx={{ px: 2.5, py: 2 }}>
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    gap={2}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" gap={0.75}>
                        <Typography sx={{ fontWeight: 600 }} noWrap>
                          {personName(item)}
                        </Typography>
                        {item.reaction ? (
                          <Typography component="span">
                            {reactionEmoji(item.reaction)}
                          </Typography>
                        ) : null}
                      </Stack>
                      {item.comment ? (
                        <Typography
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.88rem",
                            mt: 0.75,
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere"
                          }}
                        >
                          {item.comment}
                        </Typography>
                      ) : null}
                    </Box>
                    <Typography
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        flexShrink: 0
                      }}
                    >
                      {formatShortDate(item.createdAt)}
                    </Typography>
                  </Stack>
                </Box>
                <Divider />
              </Box>
            ))
          )}
        </Box>
      </Stack>
    </Drawer>
  )
}
